import { useMemo } from "react";
import { useParams } from "react-router-dom";
import TakeAssignmentPage from "./components/TakeAssignmentPage";
import TakeQuizPage from "./components/TakeQuizPage";
import styles from "./index.module.css";
import PortalTopBar from "../portal-top-bar";
import { ConditionalWrapper, ThemeWrapper, WelcomeStep } from "@verity/components";
import { useIsInstructor } from "../../shared";
import {
	baseTransKey,
	ErrorPageKey,
	getStartAssessmentDisabledMessage,
	isErrorWelcomePageKey,
} from "./takeAssessmentHelper";
import TakeAssessmentErrorBox, { WarningContainer } from "./components/TakeAssessmentErrorBox";
import TakeAssessmentInfo from "./components/TakeAssessmentInfo";
import { CoreCompleteCard, CorePaper } from "@verity/components/core";
import useFetchAssessmentInfo from "./hooks/useFetchAssessmentInfo";
import usePreAssessmentCheck, { PreAssessmentCheckReturn } from "./hooks/usePreAssessmentCheck";
import i18n from "../../../i18n";
import useQuizTimer, { QuizTimerStatus } from "./hooks/useQuizTimer";
import LandingPage from "./components/LandingPage";
import TakeAssessmentFooter from "./components/TakeAssessmentFooter";
import { Stack } from "@mui/material";

export type SharedTakeAssessmentProps = {
	quizName: string;
	proctoringSessionId: string;
} & Pick<PreAssessmentCheckReturn, "registerSubmitFn"> &
	Pick<QuizTimerStatus, "timerParts" | "timerStatus">;

export default function TakeAssessmentPage() {
	const { quizId } = useParams();
	const isInstructor = useIsInstructor();

	const {
		handleStartPreCheck,
		handleSubmitAssessment,
		proctoringSessionId,
		hasStartedPreCheck,
		isStoppingProctoring,
		hasListenersLoaded,
		showFinishUI,
		handleFinish,
		registerSubmitFn,
		isExempted,
		quizScore,
	} = usePreAssessmentCheck();

	const {
		fetching,
		verityAssessment,
		isDeleted,
		quizName,
		courseName,
		assessmentInfo,
		isValidNumAttempts,
		sessionStartTime,
		sessions,
	} = useFetchAssessmentInfo(quizId, proctoringSessionId);

	const { timerStatus, windowStatus, timerParts } = useQuizTimer(
		verityAssessment,
		sessionStartTime,
		isInstructor,
		handleSubmitAssessment,
	);

	const errorPageKey: ErrorPageKey | null = useMemo(() => {
		if (!quizId || !verityAssessment) return "noAssessmentFound";
		if (isDeleted) return "isDeleted";

		// Instructor can view even if the below errors are true
		if (isInstructor) return null;

		if (!verityAssessment.isPublished) return "notPublished";
		if (windowStatus.isBeforeWindow) return "beforeStart";
		if (windowStatus.isAfterWindow) return "afterEnd";
		if (!isValidNumAttempts) return "attemptsExceeded";
		return null;
	}, [quizId, verityAssessment, isDeleted, isInstructor, windowStatus, isValidNumAttempts]);

	const sharedProps = useMemo(() => {
		return {
			registerSubmitFn,
			quizName,
			timerParts,
			timerStatus,
		};
	}, [registerSubmitFn, quizName, timerParts, timerStatus]);

	const assessmentInfoUI = useMemo(() => {
		return (
			<TakeAssessmentInfo
				quizName={quizName}
				courseName={courseName}
				description={verityAssessment?.description ?? undefined}
			/>
		);
	}, [courseName, quizName, verityAssessment?.description]);

	const renderLandingPage = useMemo(() => {
		if (proctoringSessionId || showFinishUI) return null;

		const isFirstTime = sessions.length === 0;

		const showWelcomeErrorPage = isErrorWelcomePageKey(errorPageKey) || (errorPageKey === "afterEnd" && isFirstTime);
		const showLanding = sessions.length > 0 && Boolean(verityAssessment);
		const showWelcome = !errorPageKey;
		const showFooter = showLanding && !showWelcomeErrorPage;

		const getContent = () => {
			if (showWelcomeErrorPage) {
				return (
					<WelcomeStep
						mainComponent={<TakeAssessmentErrorBox type={errorPageKey} windowStatus={windowStatus} />}
						showDescription={false}
						showTitle={false}
						showImage={errorPageKey !== "beforeStart"}
						slotProps={{
							container: {
								style: {
									height: "unset",
								},
							},
						}}
					/>
				);
			}

			// Show landing page with all sessions. Errors for attemptsExceeded and afterEnd is here. Technically verity assessment is always defined
			if (showLanding && verityAssessment) {
				return (
					<LandingPage assessmentInfoUI={assessmentInfoUI} sessions={sessions} verityAssessment={verityAssessment} />
				);
			}

			// First time and no errors
			if (showWelcome) {
				return (
					<WelcomeStep
						title={isExempted ? (i18n.t(`${baseTransKey}.exemptedTitle`) as string) : undefined}
						onStart={() => handleStartPreCheck(assessmentInfo)}
						mainComponent={
							<Stack spacing={1} width="100%">
								{assessmentInfoUI}
								{windowStatus.isLateSubmission && (
									<WarningContainer>{i18n.t(`${baseTransKey}.lateSubmission`)}</WarningContainer>
								)}
							</Stack>
						}
						showSkeleton={!assessmentInfo || !hasListenersLoaded}
						slotProps={{
							startButton: {
								disabled: hasStartedPreCheck,
								loading: hasStartedPreCheck,
							},
							container: {
								style: {
									height: "unset",
								},
							},
						}}
						showTitle={false}
						showDescription={!isExempted}
					/>
				);
			}

			return null;
		};

		const content = getContent();
		if (!content) return null;

		return (
			<ConditionalWrapper
				condition={showFooter}
				whenTrue={(children) => (
					<>
						{children}
						<TakeAssessmentFooter
							disabled={hasStartedPreCheck || !hasListenersLoaded || !!errorPageKey || !assessmentInfo}
							loading={hasStartedPreCheck}
							onSubmit={() => handleStartPreCheck(assessmentInfo)}
							disabledTooltip={getStartAssessmentDisabledMessage(
								errorPageKey,
								assessmentInfo,
								hasListenersLoaded,
								hasStartedPreCheck,
							)}
							text={i18n.t(`${baseTransKey}.start`) as string}
						/>
					</>
				)}
			>
				<CorePaper className={styles.welcomePaper}>{content}</CorePaper>
			</ConditionalWrapper>
		);
	}, [
		assessmentInfo,
		assessmentInfoUI,
		errorPageKey,
		handleStartPreCheck,
		hasListenersLoaded,
		hasStartedPreCheck,
		isExempted,
		proctoringSessionId,
		sessions,
		showFinishUI,
		verityAssessment,
		windowStatus,
	]);

	if (fetching) {
		return <WelcomeStep showSkeleton />;
	}

	return (
		<ThemeWrapper scoped={false}>
			<div className={styles.takeAssessmentPage}>
				<PortalTopBar />
				<div className={styles.takeAssessmentContent}>
					{renderLandingPage}

					{showFinishUI &&
						(() => {
							const isQuiz = verityAssessment?.type === "quiz";
							return (
								<CoreCompleteCard
									className={`${styles.completeCard} ${!isQuiz ? styles.assignment : ""}`}
									title={i18n.t(`${baseTransKey}.finishTitle`) as string}
									description={i18n.t(`${baseTransKey}.finishSubtitle`) as string}
									buttonText={i18n.t(`${baseTransKey}.finishButton`) as string}
									onClick={handleFinish}
								>
									{isQuiz && quizScore !== null && Boolean(verityAssessment?.showScoreOnSubmit) && (
										<div className={styles.scoreBox}>
											<span className={styles.scoreLabel}>{i18n.t(`${baseTransKey}.yourResult`)}</span>
											<span className={styles.scoreValue}>
												{quizScore.earned} / {quizScore.total}
											</span>
										</div>
									)}
								</CoreCompleteCard>
							);
						})()}

					{!errorPageKey && proctoringSessionId && verityAssessment && !showFinishUI && (
						<>
							{verityAssessment.type === "assignment" && (
								<TakeAssignmentPage
									{...sharedProps}
									assessment={verityAssessment}
									proctoringSessionId={proctoringSessionId}
								/>
							)}
							{verityAssessment.type === "quiz" && (
								<TakeQuizPage {...sharedProps} proctoringSessionId={proctoringSessionId} />
							)}
							<TakeAssessmentFooter
								onSubmit={() => handleSubmitAssessment(false)}
								disabled={isStoppingProctoring}
								loading={isStoppingProctoring}
							/>
						</>
					)}
				</div>
			</div>
		</ThemeWrapper>
	);
}
